/* Cached bytes that belong to one identity, and getting rid of them when the
   identity changes.

   The service worker never caches /api/*, so no library data survives a sign-out
   — but /thumbs/ is cache-first and gated, which makes a thumbnail the one
   user-scoped thing on disk. Sign out, sign in as someone else, and their
   library renders with the previous account's images in it.

   Two rules shape this:

   - **Thumbnails only.** The shell, the bundle and the fonts are byte-identical
     for every user; clearing them would re-download the app to fix nothing.
   - **Identity is `AS ?? email`, not `email`.** An admin viewing another library
     through `?as=` caches that user's thumbnails, and dropping the param has to
     count as a change of identity or those images stay.

   App-side rather than in the worker: `caches` is on `window` too, so there is
   no message channel to keep in sync, and the worker's fetch handler stays a
   thing you can read top to bottom. */

import { AS } from './api.js';

const THUMBS = 'grain-thumbs-v1';

/** Last identity these caches were filled for. */
const STORED = 'grain:identity';

/** Who the cached bytes belong to, from the app's point of view. */
const identityKey = (email: string): string => AS ?? email;

/**
 * Drop every cache scoped to the current user.
 *
 * Await it before navigating away on sign-out — `location.assign` will otherwise
 * tear the page down mid-delete. Failures are swallowed: a browser that denies
 * CacheStorage (or has none, on an insecure origin) is not a reason to block
 * signing out.
 */
export async function purgeUserCaches(): Promise<void> {
  try {
    await caches?.delete(THUMBS);
  } catch {
    /* no CacheStorage here, or storage denied — nothing cached to leak either */
  }
  try {
    localStorage.removeItem(STORED);
  } catch {
    /* private mode */
  }
}

/**
 * Note who the app is now showing, and purge if that differs from last time.
 *
 * Called on every path into a session, so it catches the cases a sign-out
 * handler cannot: a cookie swapped underneath the app, an `?as=` link opened
 * directly, or a sign-out that never completed because the tab was closed.
 *
 * A first run with nothing stored records the identity without purging —
 * an existing install's thumbnails are its own until proven otherwise.
 */
export async function reconcileIdentity(email: string): Promise<void> {
  const key = identityKey(email);
  let prev: string | null;
  try {
    prev = localStorage.getItem(STORED);
  } catch {
    return; // no storage, so no way to tell one identity from the next
  }
  if (prev === key) return;
  if (prev !== null) await purgeUserCaches();
  try {
    localStorage.setItem(STORED, key);
  } catch {
    /* private mode: every load looks like a first run, which is safe */
  }
}
